import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Dialog,
  Chip,
  Stack,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import InfoIcon from '@mui/icons-material/Info';
import ImageIcon from '@mui/icons-material/Image';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

interface RentalImage {
  link: string;
  category?: string;
  note?: string | null;
}

interface RentalImageGalleryProps {
  images: RentalImage[];
  title?: string;
}

const RentalImageGallery: React.FC<RentalImageGalleryProps> = ({
  images,
  title = 'Rental Images',
}) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const [openModal, setOpenModal] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handlePrevImage = useCallback(() => {
    setSelectedImageIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  }, [images.length]);

  const handleNextImage = useCallback(() => {
    setSelectedImageIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  }, [images.length]);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -300,
        behavior: 'smooth',
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 300,
        behavior: 'smooth',
      });
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!openModal) return;
      
      if (e.key === 'ArrowLeft') {
        handlePrevImage();
      } else if (e.key === 'ArrowRight') {
        handleNextImage();
      } else if (e.key === 'Escape') {
        handleCloseModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [openModal, handlePrevImage, handleNextImage]);

  const CATEGORY_LABELS: Record<string, string> = {
    // Pre-rental check
    start_issue_safe_to_go: 'Pre-ride: Safe to Go',
    start_issue_cosmetic_damage: 'Pre-ride: Cosmetic Damage',
    start_issue_unsafe_to_use: 'Pre-ride: Unsafe to Use',
    // Kayak hull check
    hull_photo_start: 'Hull Check (Start)',
    hull_photo_end: 'Hull Check (End)',
    // Kayak damage
    damage_photo_start: 'Damage Report (Start)',
    damage_photo_end: 'Damage Report (End)',
    // Lock photos
    noke: 'Noke Lock Photo',
    noke_start_rental: 'Noke Photo (Start)',
    noke_mid_rental: 'Noke Photo (Mid-rental)',
    axa: 'AXA Lock Photo',
    // End rental
    'End Rental Photo': 'End Rental Photo',
  };

  const getCategoryLabel = (category?: string): string => {
    if (!category) return '';
    return CATEGORY_LABELS[category] ?? category;
  };

  const getCategoryColor = (category?: string) => {
    if (!category) return 'default';
    const categoryLower = category.toLowerCase();
    // Check specific cases before broad patterns
    if (categoryLower.includes('unsafe')) return 'error';
    if (categoryLower.includes('cosmetic')) return 'warning';
    if (categoryLower.includes('safe_to_go')) return 'success';
    if (categoryLower.includes('damage')) return 'error';
    if (categoryLower.includes('inspection')) return 'warning';
    if (categoryLower.includes('before') || categoryLower.includes('start')) return 'info';
    if (categoryLower.includes('after') || categoryLower.includes('end')) return 'success';
    return 'primary';
  };

  if (!images || images.length === 0) {
    return (
      <Card>
        <CardContent>
          <Stack spacing={2} alignItems="center" sx={{ py: 3 }}>
            <ImageIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              No images available for this rental
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  const selectedImage = images[selectedImageIndex];

  return (
    <>
      <Card>
        <CardContent sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" component="h2">
                {title}
              </Typography>
              <Chip
                label={`${images.length} ${images.length === 1 ? 'image' : 'images'}`}
                size="small"
                variant="outlined"
              />
            </Box>

            {/* Horizontal scrollable gallery */}
            <Box sx={{ position: 'relative' }}>
              {/* Left scroll button */}
              {images.length > 3 && (
                <IconButton
                  onClick={scrollLeft}
                  sx={{
                    position: 'absolute',
                    left: -16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 2,
                    bgcolor: 'background.paper',
                    boxShadow: 2,
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  size="small"
                >
                  <NavigateBeforeIcon fontSize="small" />
                </IconButton>
              )}

              {/* Right scroll button */}
              {images.length > 3 && (
                <IconButton
                  onClick={scrollRight}
                  sx={{
                    position: 'absolute',
                    right: -16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 2,
                    bgcolor: 'background.paper',
                    boxShadow: 2,
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  size="small"
                >
                  <NavigateNextIcon fontSize="small" />
                </IconButton>
              )}

              {/* Scrollable container */}
              <Box
                ref={scrollContainerRef}
                sx={{
                  display: 'flex',
                  gap: 2,
                  overflowX: 'auto',
                  scrollBehavior: 'smooth',
                  pb: 1,
                  // Hide scrollbar but keep functionality
                  '&::-webkit-scrollbar': {
                    height: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'grey.200',
                    borderRadius: '3px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'grey.400',
                    borderRadius: '3px',
                    '&:hover': {
                      backgroundColor: 'grey.500',
                    },
                  },
                }}
              >
                {images.map((image, index) => (
                  <Box
                    key={index}
                    sx={{
                      position: 'relative',
                      flexShrink: 0,
                      width: isMobile ? '250px' : '300px',
                      height: '200px',
                      borderRadius: 1,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      transition: 'transform 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.02)',
                        '& .image-overlay': {
                          opacity: 1,
                        },
                      },
                    }}
                    onClick={() => handleImageClick(index)}
                  >
                    <img
                      src={image.link}
                      alt={image.category || `Rental image ${index + 1}`}
                      loading="lazy"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.onerror = null;
                        target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="200" viewBox="0 0 300 200"%3E%3Crect width="300" height="200" fill="%23f5f5f5"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="14" fill="%23999"%3EImage not available%3C/text%3E%3C/svg%3E';
                      }}
                    />
                    
                    {/* Overlay with category and info */}
                    <Box
                      className="image-overlay"
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)',
                        p: 1.5,
                        opacity: 0.9,
                        transition: 'opacity 0.3s ease',
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        {image.category && (
                          <Chip
                            label={getCategoryLabel(image.category)}
                            size="small"
                            color={getCategoryColor(image.category) as any}
                            sx={{ height: 22 }}
                          />
                        )}
                        <Stack direction="row" spacing={0.5}>
                          {image.note && (
                            <Tooltip title="Has note">
                              <InfoIcon sx={{ color: 'white', fontSize: 18 }} />
                            </Tooltip>
                          )}
                          <Tooltip title="View fullscreen">
                            <FullscreenIcon sx={{ color: 'white', fontSize: 18 }} />
                          </Tooltip>
                        </Stack>
                      </Stack>
                      {image.note && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'white',
                            display: 'block',
                            mt: 0.5,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {image.note}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Fullscreen Modal with Navigation */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            height: '90vh',
            maxHeight: '90vh',
            width: '95vw',
            m: 'auto',
            overflow: 'hidden',
          },
        }}
      >
        <Box sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Header with close button and image counter */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              zIndex: 2,
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
            }}
          >
            <Chip
              label={`${selectedImageIndex + 1} / ${images.length}`}
              sx={{
                bgcolor: 'rgba(0,0,0,0.5)',
                color: 'white',
              }}
            />
            <IconButton
              aria-label="close"
              onClick={handleCloseModal}
              sx={{
                color: 'white',
                bgcolor: 'rgba(0,0,0,0.5)',
                '&:hover': {
                  bgcolor: 'rgba(0,0,0,0.7)',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Navigation arrows */}
          {images.length > 1 && (
            <>
              <IconButton
                onClick={handlePrevImage}
                sx={{
                  position: 'absolute',
                  left: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                  bgcolor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(0,0,0,0.7)',
                  },
                }}
              >
                <NavigateBeforeIcon fontSize="large" />
              </IconButton>

              <IconButton
                onClick={handleNextImage}
                sx={{
                  position: 'absolute',
                  right: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                  bgcolor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(0,0,0,0.7)',
                  },
                }}
              >
                <NavigateNextIcon fontSize="large" />
              </IconButton>
            </>
          )}

          {selectedImage && (
            <>
              {/* Image display area - takes remaining space */}
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0, // Important for flex to work properly
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  bgcolor: 'grey.900',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <img
                  src={selectedImage.link}
                  alt={selectedImage.category || 'Rental image'}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.onerror = null;
                    target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"%3E%3Crect width="400" height="300" fill="%23333"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="18" fill="%23999"%3EImage not available%3C/text%3E%3C/svg%3E';
                  }}
                />
              </Box>

              {/* Bottom info bar - fixed height */}
              {(selectedImage.category || selectedImage.note) && (
                <Box
                  sx={{
                    flexShrink: 0,
                    maxHeight: '120px',
                    overflow: 'auto',
                    p: 1.5,
                    bgcolor: 'background.paper',
                    borderTop: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Stack spacing={0.5}>
                    {selectedImage.category && (
                      <Box>
                        <Chip
                          label={getCategoryLabel(selectedImage.category)}
                          color={getCategoryColor(selectedImage.category) as any}
                          size="small"
                        />
                      </Box>
                    )}
                    {selectedImage.note && (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <InfoIcon sx={{ color: 'text.secondary', fontSize: 18, mt: 0.25, flexShrink: 0 }} />
                        <Typography
                          variant="body2"
                          color="text.primary"
                          sx={{
                            lineHeight: 1.4,
                            wordBreak: 'break-word'
                          }}
                        >
                          {selectedImage.note}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Box>
              )}
              {images.length > 1 && (
                <Box
                  sx={{
                    flexShrink: 0,
                    display: 'flex',
                    gap: 0.5,
                    p: 0.5,
                    bgcolor: 'background.paper',
                    borderTop: 1,
                    borderColor: 'divider',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    '&::-webkit-scrollbar': {
                      height: '4px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: 'grey.200',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: 'grey.400',
                    },
                  }}
                >
                  {images.map((image, index) => (
                    <Box
                      key={index}
                      onClick={() => setSelectedImageIndex(index)}
                      sx={{
                        flexShrink: 0,
                        width: 60,
                        height: 45,
                        borderRadius: 0.5,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        border: selectedImageIndex === index ? 2 : 0,
                        borderColor: 'primary.main',
                        opacity: selectedImageIndex === index ? 1 : 0.6,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          opacity: 1,
                        },
                      }}
                    >
                      <img
                        src={image.link}
                        alt={`Thumbnail ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.onerror = null;
                          target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="60" height="45" viewBox="0 0 60 45"%3E%3Crect width="60" height="45" fill="%23f5f5f5"/%3E%3C/svg%3E';
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </>
          )}
        </Box>
      </Dialog>
    </>
  );
};

export default RentalImageGallery;